import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Dayjs } from 'dayjs';
import { Box, ClosableDrawer, Grid, PrimaryPaper } from '@atoms/layout';
import { Chip, Divider, Stack, styled, Typography } from '@mui/material';
import { Button } from '@atoms/buttons';
import { useJobQualificationsData } from '@libs/store/talents';
import { useStaffingPartnersList } from '@libs/store/staffingPartners';
import { StaffingPartnerList } from '@libs/models/staffingPartners';
import { useShiftsAssignment, useShareDrawerStore } from '@libs/store/shifts';
import { TIMEZONE_FALLBACK } from '@libs/config';
import { useLocationStore } from '@libs/store/locations';
import { useNotification } from '@libs/snackbar';
import { LocationExtra } from '@libs/models/locations';
import { ShiftsIcon } from '@atoms/icons';
import { DataGrid, EmptyListPlaceholder } from '@molecules/dataDisplay';
import { useShiftListColumns } from './shiftListColumns';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { Checkbox, FilterMultiSelect, renderCheckboxOption, SearchField } from '@atoms/inputs';
import { useStaffingPartnersShortListColumns } from '@pages/staffingPartners/listPage/table/columns';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { useSeriesNRotationsListColumns } from './recurrencesListColumns';
import { parseError } from '@libs/api/errors';
import { StartDateRangeFilterInputControlled } from '@molecules/filters';

type DrawerProps = {
  onSaveCall?: () => void;
};
const FallbackTimezone = TIMEZONE_FALLBACK;
export function ShareShifts({ onSaveCall }: DrawerProps) {
  const { showErrorIntl } = useNotification();

  const { closeShiftShareDrawer, setCurrentStep } = useShareDrawerStore();
  const drawerOpen = useShareDrawerStore((s) => s.drawerOpen);
  const shiftLoading = useShareDrawerStore((s) => s.loading);
  const selectedShifts = useShareDrawerStore((s) => s.selectedShifts);
  const currentStep = useShareDrawerStore((s) => s.currentStep);
  const shiftsPassedFromOutside = useShareDrawerStore((s) => s.shiftsPassedFromOutside);
  const selectedSeriesNRotations = useShareDrawerStore((s) => s.selectedSeriesNRotations);
  const seriesNRotationsLoading = useShareDrawerStore((s) => s.seriesNRotationsLoading);

  const staffingPartnersLoading = useStaffingPartnersList((s) => s.loadingList);
  const { shareShifts } = useShiftsAssignment();
  const { current: currentLocation, loading: locationsLoading, currentLocationID } = useLocationStore();

  const [selectedAgencies, setSelectedAgencies] = useState<GridRowSelectionModel>([]);
  const [sharingProgress, setSharingProgress] = useState(false);
  const [allowMultipleAssignees, setAllowMultipleAssignees] = useState(true);

  const localizationSubset = 'shifts.share.drawer';
  const open = drawerOpen;

  const loading =
    shiftLoading || staffingPartnersLoading || locationsLoading || sharingProgress || seriesNRotationsLoading;

  const handleClose = () => {
    closeShiftShareDrawer();
  };

  const handleSubmit = async () => {
    setSharingProgress(true);
    try {
      await shareShifts(
        Array.from(new Set([...selectedShifts, ...selectedSeriesNRotations])),
        selectedAgencies as string[],
        allowMultipleAssignees,
      );
      onSaveCall && onSaveCall();
      handleClose();
    } catch (e) {
      if (e instanceof Error) {
        const error = parseError(e, `shifts.shareErrors`);
        showErrorIntl(error.message);
        // showError(error.message);
      }
    }
    setSharingProgress(false);
  };

  const handleNextPage = async () => {
    if (currentStep === 'shifts') {
      setCurrentStep('agencies');
    } else {
      await handleSubmit();
    }
  };
  const handlePrevPage = () => {
    if (shiftsPassedFromOutside) {
      handleClose();
      return;
    }

    if (currentStep === 'agencies') {
      setCurrentStep('shifts');
    } else {
      handleClose();
    }
  };
  const disableProceedShiftsPage = loading || (!selectedShifts.length && !selectedSeriesNRotations.length);
  const disableProceedAgenciesPage = disableProceedShiftsPage || !selectedAgencies.length;

  return (
    <ClosableDrawer
      open={open}
      loading={loading}
      onClose={handleClose}
      disableControlsDivider
      controls={
        <>
          <Divider sx={(theme) => ({ marginBottom: theme.spacing(2) })} />
          <Stack direction={'row-reverse'} spacing={2} sx={(theme) => ({ paddingBottom: theme.spacing(1) })}>
            <MenuButton
              variant="contained"
              onClick={handleNextPage}
              disabled={currentStep === 'shifts' ? disableProceedShiftsPage : disableProceedAgenciesPage}
            >
              <FormattedMessage
                id={`${localizationSubset}.menuButtons.${currentStep === 'agencies' ? 'share' : 'proceed'}.label`}
              />
            </MenuButton>
            <Box flex={1} />
            <MenuButton
              onClick={handlePrevPage}
              sx={(theme) => ({ color: theme.palette.text.secondary })}
              disabled={loading}
            >
              <FormattedMessage id={`${localizationSubset}.menuButtons.back.label`} />
            </MenuButton>
          </Stack>
        </>
      }
    >
      <Stack direction={'column'} sx={{ height: '100%' }} spacing={2} width={{ xs: '400px', sm: '700px' }}>
        {/* Header */}
        <Stack direction={'row'}>
          <Typography variant="h5">
            <FormattedMessage id={`${localizationSubset}.dialogHeading`} />
          </Typography>
          <Box flex={1} />
        </Stack>

        {currentStep === 'shifts' && (
          <ShiftsPage currentLocation={currentLocation} localizationSubset={localizationSubset} />
        )}
        {currentStep === 'agencies' && (
          <AgenciesPage
            onSelectionChange={setSelectedAgencies}
            shiftsSelected={selectedShifts.length}
            seriesNRotationsSelected={selectedSeriesNRotations.length}
            localizationSubset={localizationSubset}
            onMultipleAssigneeChanged={(v) => setAllowMultipleAssignees(v)}
            allowMultipleAssignees={allowMultipleAssignees}
          />
        )}
      </Stack>
    </ClosableDrawer>
  );
}

type DateFilterValue = { key: string; value: { from: Dayjs; to: Dayjs; timezone: string } };
type ShiftsPageProps = {
  currentLocation: LocationExtra | null;
  localizationSubset: string;
};

function ShiftsPage({ currentLocation, localizationSubset }: ShiftsPageProps) {
  const intl = useIntl();
  const { loadSharableShifts, setSelectedShifts, loadSharableSeriesNRotations, setSelectedSeriesNRotations } =
    useShareDrawerStore();
  const shiftsToShare = useShareDrawerStore((s) => s.shiftsToShare);
  const seriesNRotationsToShare = useShareDrawerStore((s) => s.seriesNRotationsToShare);
  const selectedShifts = useShareDrawerStore((s) => s.selectedShifts);
  const selectedSeriesNRotations = useShareDrawerStore((s) => s.selectedSeriesNRotations);
  const seriesNRotationsLoading = useShareDrawerStore((s) => s.seriesNRotationsLoading);

  const totalSingleShifts = useShareDrawerStore((s) => s.totalShifts);
  const singleShiftsLoading = useShareDrawerStore((s) => s.loading);

  const loading = singleShiftsLoading || seriesNRotationsLoading;
  localizationSubset = `${localizationSubset}.shiftsPickPage`;

  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [dateFilter, setDateFilter] = useState<DateFilterValue | null>(null);
  const [qualifications, setQualifications] = useState<string[]>([]);
  const [currentTab, setCurrentTab] = useState<'single' | 'seriesNRotations'>('single');

  const handleChange = (event: React.SyntheticEvent, newValue: 'single' | 'seriesNRotations') => {
    setCurrentTab(newValue);
  };
  const dateLabel = [dateFilter?.value.from?.format('MMM D, YYYY'), dateFilter?.value.to?.format('MMM D, YYYY')]
    .filter(Boolean)
    .join(' - ');

  const { data } = useJobQualificationsData();
  const qualificationsOptions = data.map((q) => ({
    label: q.abbreviation,
    value: q.id,
  }));

  const singleShiftRows = shiftsToShare.filter((row) => row.recurrence === 'single');

  useEffect(() => {
    if (!currentLocation) {
      return;
    }
    loadSharableShifts({
      dateFilter: dateFilter?.value || null,
      statusFilter,
      locationId: currentLocation?.id,
      qualifications,
    });
    loadSharableSeriesNRotations({
      dateFilter: dateFilter?.value || null,
      statusFilter,
      locationId: currentLocation?.id,
      qualifications,
    });
  }, [currentLocation, statusFilter, dateFilter?.value, qualifications]);

  const statusFilterOptions = ['Unassigned', 'Pending', 'Unfilled'].map((status) => ({
    label: status,
    value: status,
  }));

  return (
    <PrimaryPaper sx={{ mt: 1, minHeight: 'calc(100% - 50px)' }}>
      <Stack direction={'column'} spacing={2} sx={{ height: '100%', maxHeight: 'calc(100% - 80px)' }}>
        <Grid container spacing={2}>
          <Grid xs={4}>
            <StartDateRangeFilterInputControlled
              loading={loading}
              timezone={currentLocation?.timezone || FallbackTimezone}
              onClear={() => setDateFilter(null)}
              onSetFilter={(filter) => setDateFilter(filter as DateFilterValue)}
              dateLabel={dateLabel}
              dateValue={dateFilter?.value}
              label={<FormattedMessage id="shifts.share.dateField.label" />}
            />
          </Grid>
          <Grid xs={4}>
            <FilterMultiSelect
              size="small"
              disabled={loading}
              options={statusFilterOptions}
              inlineLabel
              fullWidth
              label={`${intl.formatMessage({ id: 'filters.statuses.label' })}:`}
              onApply={(v) => setStatusFilter(v)}
              onClear={() => setStatusFilter([])}
              value={statusFilter ?? []}
              renderOption={renderCheckboxOption}
            />
          </Grid>
          <Grid xs={4}>
            <FilterMultiSelect
              size="small"
              inlineLabel
              label={<FormattedMessage id="talents.qualification" />}
              fullWidth
              renderOption={renderCheckboxOption}
              options={qualificationsOptions}
              onApply={(v) => setQualifications(v)}
              onClear={() => setQualifications([])}
              value={qualifications}
            />
          </Grid>
        </Grid>
        <Stack direction={'column'} sx={{ height: '100%', minHeight: 'calc(100% - 80px)' }}>
          <TabContext value={currentTab}>
            <TabList onChange={handleChange}>
              <Tab label={intl.formatMessage({ id: `${localizationSubset}.single.title` })} value="single" />
              <Tab
                label={intl.formatMessage({ id: `${localizationSubset}.seriesNRotations.title` })}
                value="seriesNRotations"
              />
            </TabList>
            <TabPanel value="single" sx={{ p: 0, py: 2, height: '100%', minHeight: '100%' }}>
              <DataGrid
                rows={singleShiftRows}
                loading={loading}
                rowHeight={64}
                hideFooter
                columns={useShiftListColumns()}
                sx={{ minHeight: '300px' }}
                checkboxSelection
                rowCount={totalSingleShifts}
                onRowSelectionModelChange={(newRowSelectionModel) => {
                  setSelectedShifts(newRowSelectionModel.map((v) => v.toString()));
                }}
                rowSelectionModel={selectedShifts}
                noRowsOverlay={
                  <EmptyListPlaceholder
                    icon={<ShiftsIcon active />}
                    title={<FormattedMessage id="shifts.table.noRows.title" />}
                    subtitle={<FormattedMessage id="shifts.table.noRows.subtitle" />}
                  />
                }
              />
            </TabPanel>
            <TabPanel value="seriesNRotations" sx={{ p: 0, py: 2, height: '100%', minHeight: '100%' }}>
              <DataGrid
                rows={seriesNRotationsToShare}
                loading={loading}
                rowHeight={64}
                hideFooter
                columns={useSeriesNRotationsListColumns()}
                sx={{ minHeight: '300px' }}
                checkboxSelection
                rowCount={totalSingleShifts}
                onRowSelectionModelChange={(newRowSelectionModel) => {
                  setSelectedSeriesNRotations(newRowSelectionModel.map((v) => v.toString()));
                }}
                rowSelectionModel={selectedSeriesNRotations}
                noRowsOverlay={
                  <EmptyListPlaceholder
                    icon={<ShiftsIcon active />}
                    title={<FormattedMessage id="shifts.table.noRows.title" />}
                    subtitle={<FormattedMessage id="shifts.table.noRows.subtitle" />}
                  />
                }
              />
            </TabPanel>
          </TabContext>
          <Stack direction={'row'} spacing={1}>
            <Chip
              disabled={loading}
              color="primary"
              label={intl.formatMessage(
                { id: `${localizationSubset}.shiftsSelectionCounter` },
                { amount: selectedShifts.length + selectedSeriesNRotations.length },
              )}
            />
          </Stack>
        </Stack>
      </Stack>
    </PrimaryPaper>
  );
}

type AgenciesPageProps = {
  shiftsSelected?: number;
  seriesNRotationsSelected?: number;
  localizationSubset: string;
  onSelectionChange?: (selection: GridRowSelectionModel) => void;
  allowMultipleAssignees?: boolean;
  onMultipleAssigneeChanged?: (value: boolean) => void;
  showSearch?: boolean;
  showHeader?: boolean;
  showSelectedAgenciesCount?: boolean;
  selectedRows?: GridRowSelectionModel;
  disableSelection?: boolean;
};

export function AgenciesPage({
  shiftsSelected,
  showSearch = true,
  showHeader = true,
  showSelectedAgenciesCount = true,
  disableSelection = false,
  seriesNRotationsSelected = 0,
  localizationSubset,
  onSelectionChange,
  allowMultipleAssignees,
  onMultipleAssigneeChanged,
  selectedRows = [],
}: AgenciesPageProps) {
  const intl = useIntl();

  const staffingPartnersLoading = useStaffingPartnersList((s) => s.loadingList);
  const rows = useStaffingPartnersList((s) => s.staffingPartners);
  const loadStaffingPartners = useStaffingPartnersList((s) => s.loadStaffingPartners);
  const { currentLocationID } = useLocationStore();

  const [search, setSearch] = useState<string>('');
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>(selectedRows);
  // `multipleAssigneesSelector` does not make sense for single shifts
  const showMultipleAssigneesSelector = seriesNRotationsSelected > 0;

  const localizationSet = `${localizationSubset}.agencyPickPage`;

  const loading = staffingPartnersLoading;

  useEffect(() => {
    loadStaffingPartners({
      statuses: { key: 'statuses', value: ['Active'] },
      location: { key: 'location', value: currentLocationID },
    });
  }, [loadStaffingPartners]);

  useEffect(() => {
    const agenciesUrls = rows.filter((row, index) => rowSelectionModel.includes(row.id)).map((row) => row.partnerUrl);
    onSelectionChange && onSelectionChange(agenciesUrls);
  }, [rowSelectionModel]);

  const rowsFiltered = search
    ? rows.filter((row) => {
        const record = JSON.stringify(row);
        if (record.includes(search)) {
          return true;
        }
      })
    : rows;

  const shouldShowHeader = showHeader || shiftsSelected !== undefined || seriesNRotationsSelected !== undefined;

  return (
    <PrimaryPaper sx={{ mt: 1, height: '100%', maxHeight: '100%' }}>
      <Stack direction={'column'} spacing={2} sx={{ height: '100%' }}>
        {shouldShowHeader && (
          <Grid container>
            {showSearch && (
              <Grid xs={6}>
                <SearchField
                  disabled={loading}
                  fullWidth
                  value={search}
                  onChange={(elem: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    setSearch(elem.target.value)
                  }
                />
              </Grid>
            )}
            {showMultipleAssigneesSelector && (
              <Grid xs={6}>
                <Checkbox
                  label={<FormattedMessage id={`${localizationSet}.allowMultipleAssignees`} />}
                  onChange={(e, checked) => onMultipleAssigneeChanged?.(checked)}
                  value={!!allowMultipleAssignees}
                />
              </Grid>
            )}
          </Grid>
        )}
        <Stack
          height="100%"
          spacing={2}
          sx={{ height: '100%', minHeight: `calc(100% - ${shouldShowHeader ? 80 : 0}px)` }}
        >
          <DataGrid
            rows={rowsFiltered}
            loading={loading}
            rowHeight={64}
            columns={useStaffingPartnersShortListColumns()}
            autoPageSize
            hideFooter
            disableVirtualization={false}
            checkboxSelection
            slotProps={{
              baseCheckbox: {
                ...(disableSelection ? { color: 'secondary' } : {}),
              },
            }}
            filterModel={{
              items: [{ field: 'name', operator: 'contains', value: search }],
            }}
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setRowSelectionModel(disableSelection ? selectedRows : newRowSelectionModel);
            }}
            rowSelectionModel={rowSelectionModel}
            noRowsOverlay={
              <EmptyListPlaceholder
                icon={<ShiftsIcon active />}
                title={<FormattedMessage id="shifts.table.noRows.title" />}
                subtitle={<FormattedMessage id="shifts.table.noRows.subtitle" />}
              />
            }
          />
          <Stack direction={'row'} spacing={1}>
            {shiftsSelected !== undefined && (
              <Chip
                disabled={loading}
                color="primary"
                label={intl.formatMessage(
                  { id: `${localizationSet}.shiftsSelectionCounter` },
                  { amount: shiftsSelected + seriesNRotationsSelected },
                )}
              />
            )}
            {showSelectedAgenciesCount && (
              <Chip
                disabled={loading}
                label={intl.formatMessage(
                  { id: `${localizationSet}.agenciesSelectionCounter` },
                  { amount: rowSelectionModel.length },
                )}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
    </PrimaryPaper>
  );
}

const MenuButton = styled(Button)(({ theme }) => ({
  minWidth: theme.spacing(16.75),
}));
